<template>
  <Scroller>
    <!-- Static sections -->
    <transition name="slide-fade" mode="out-in">
      <ScrollerSection id="intro" sticky="center" key="intro" v-if="!active">
        <div>
          <h1 class="f-title-5">What is the outlook for coal and bioenergy?</h1>
          <ScrollIcon>Keep scrolling to read more</ScrollIcon>
        </div>
      </ScrollerSection>
      <ScrollerSection
        id="coalChartChina"
        key="coalChartChina"
        sticky="left"
        behind
        v-if="
          ['ChinaCoalSTEPS', 'ChinaCoalAPS', 'ChinaCoalNZE'].includes(active)
        "
      >
        <CoalCompare
          :historic="coalChart.historic"
          :scenario="coalChart[active]"
          :max="6000"
        />
      </ScrollerSection>
      <ScrollerSection
        id="coalPiles"
        key="coalPiles"
        sticky="right"
        v-if="['coal2021', 'coalSTEPS', 'coalAPS', 'coalNZE'].includes(active)"
      >
        <CoalPile
          title="Steam"
          :value="coalPiles[active].steam"
          :max="5000"
          show-value
          :change="coalPiles[active].steamChange"
          :year="coalPiles[active].year"
          :color="coalPiles[active].color"
        />
        <CoalPile
          title="Coking"
          :value="coalPiles[active].coking"
          :max="5000"
          show-value
          :change="coalPiles[active].cokingChange"
          :color="coalPiles[active].color"
        />
      </ScrollerSection>
      <ScrollerSection
        id="biomassCharts"
        key="biomassCharts"
        sticky="left"
        behind
        v-if="['biomass', 'biomassAPS', 'biomassNZE'].includes(active)"
      >
        <AreaColumn
          :scenario="
            ['STEPS', 'APS', 'NZE'][
              ['biomass', 'biomassAPS', 'biomassNZE'].indexOf(active)
            ]
          "
          :max="90"
          show-totals
          :chartData="biomassArea"
        >
          <template #legend>
            <StaticLegendTest
              :items="[
                {
                  itemsTitle: '',
                  items: [
                    { label: 'Traditional use of biomass', color: 'historic' },
                  ],
                },
                {
                  itemsTitle: 'Modern bioenergy',
                  items: [
                    { label: 'Buildings and agriculture', color: 'NZE' },
                    { label: 'Industry', color: 'yellow' },
                    { label: 'Power', color: 'blue' },
                    { label: 'Conversion losses', color: 'gray' },
                  ],
                },
              ]"
            />
          </template>
        </AreaColumn>
      </ScrollerSection>
    </transition>
    <!-- Normal sections -->
    <ScrollerSection
      id="ChinaCoalSTEPS"
      @enter="handleEnter"
      @leave="handleLeave"
    >
      <Block>
        <h4 class="f-title-5">
          Future coal use depends heavily on efforts to address climate change
        </h4>
        <p>
          Global coal demand rebounded strongly in 2021 to 5 640 million tonnes
          of coal equivalent (Mtce) as economies recovered from the pandemic and
          coal-fired power generation reached a historic high in 2021. The
          outlook for coal is heavily dependent on the strength of the world’s
          resolve to address climate change.
        </p>
        <p>
          In the <strong>Stated Policies Scenario (STEPS)</strong>, global coal
          demand declines by around 30% to 2050 but declines are much faster in
          advanced economies.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="ChinaCoalAPS" @enter="handleEnter">
      <Block>
        <p>
          In the <strong>Announced Pledges Scenario (APS)</strong>, global coal
          demand falls by 20% to 2030 and by 70% to 2050. Demand in advanced
          economies declines by 65% to 2030 as coal use in the power and
          industry sectors falls rapidly. Coal demand peaks in China in the
          early 2020s and in India in the late 2020s.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="ChinaCoalNZE" @enter="handleEnter">
      <Block>
        <p>
          In the <strong>NZE Scenario</strong>, global coal demand falls by 45%
          to 2030, with a 75% decline in advanced economies and a 40% decline in
          emerging market and developing economies. Unabated coal use drops by
          99% between 2021 and 2050, and in 2050 just under 90% of remaining
          coal power generation comes from plants equipped with CCUS.
        </p>
        <p>
          China remains by far the most influential market for coal throughout
          the outlook.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="coal2021" @enter="handleEnter">
      <LeftBlock
        title="Steam coal declines further and faster than coking coal"
      />
    </ScrollerSection>
    <ScrollerSection id="coalSTEPS" @enter="handleEnter">
      <LeftBlock>
        <p>
          Steam coal is mainly used for heat and electricity where it is
          increasingly replaced by renewables. Declines in coking coal are
          smaller than in steam coal in all scenarios, because coking coal is
          mainly used in steel making where there are fewer readily available
          alternatives.
        </p>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="coalAPS" @enter="handleEnter">
      <LeftBlock>
        <p>
          In the <strong>APS</strong>, steam coal falls by just over 20% to 2030
          as a result of efforts to reduce emissions from the power sector,
          while the declines in coking coal mainly stem from fuel switching and
          energy efficiency improvements.
        </p>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="coalNZE" @enter="handleEnter">
      <LeftBlock>
        <p>In the NZE, both steam and coking coal decline rapidly to 2030.</p>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="biomass" @enter="handleEnter">
      <Block>
        <h4 class="f-title-5">
          Traditional use of biomass gives way to modern solid bioenergy
        </h4>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="biomassAPS" @enter="handleEnter">
      <Block>
        <p>
          Traditional use of biomass falls as people gain access to clean
          cooking, while modern bioenergy grows, mainly in the power sector and
          for biofuel production.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="biomassNZE" @enter="handleEnter">
      <Block>
        <p>
          In the NZE Scenario, bioenergy resources are responsibly managed and
          do not compete with other land uses.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="fin" @enter="handleEnter" @leave="handleLeave" />
  </Scroller>
</template>

<script>
import {
  Scroller,
  ScrollerSection,
  ScrollIcon,
  Block,
} from "@international-energy-agency/vue-ui";
import LeftBlock from "../components/LeftBlock.vue";
import CoalPile from "../components/CoalPile.vue";
import AreaColumn from "../components/AreaColumn.vue";
import StaticLegendTest from "../components/StaticLegendTest.vue";
import CoalCompare from "../components/CoalCompare.vue";
import ActiveMixin from "../mixins/active";
export default {
  components: {
    ScrollerSection,
    ScrollIcon,
    Scroller,
    Block,
    LeftBlock,
    AreaColumn,
    CoalPile,
    CoalCompare,
    StaticLegendTest,
  },
  mixins: [ActiveMixin],
  data() {
    return {
      coalChart: {
        historic: {
          advanced: 1024,
          EMDE: 1463,
          China: 3154,
        },
        ChinaCoalSTEPS: {
          name: "STEPS 2050",
          advanced: 297,
          EMDE: 1665,
          China: 1866,
        },
        ChinaCoalAPS: {
          name: "APS 2050",
          advanced: 127,
          EMDE: 697,
          China: 789,
        },
        ChinaCoalNZE: {
          name: "NZE 2050",
          advanced: 84,
          EMDE: 127,
          China: 328,
        },
      },
      coalPiles: {
        coal2021: {
          year: "2021",
          steam: 4560,
          coking: 1030,
        },
        coalSTEPS: {
          year: "STEPS 2030",
          steam: 4560,
          steamChange: -0.12,
          coking: 1030,
          cokingChange: -0.09,
          color: "STEPS",
        },
        coalAPS: {
          year: "APS 2030",
          steam: 4560,
          steamChange: -0.22,
          coking: 1030,
          cokingChange: -0.17,
          color: "APS",
        },
        coalNZE: {
          year: "NZE 2030",
          steam: 4560,
          steamChange: -0.5,
          coking: 1030,
          cokingChange: -0.3,
          color: "NZE",
        },
      },
      biomassArea: {
        STEPS: [
          {
            name: "Traditional use of biomass",
            data: [
              [2021, 24],
              [2030, 20],
              [2050, 18],
            ],
            className: "color-historic",
          },
          {
            name: "Buildings and agriculture",
            data: [
              [2021, 4.8],
              [2030, 6],
              [2050, 7],
            ],
            className: "color-green",
          },
          {
            name: "Industry",
            data: [
              [2021, 11],
              [2030, 13],
              [2050, 16],
            ],
            className: "color-yellow",
          },
          {
            name: "Power",
            data: [
              [2021, 9.7],
              [2030, 13.6],
              [2050, 19.9],
            ],
            className: "color-blue",
          },
          {
            name: "Conversion losses",
            data: [
              [2021, 10.3],
              [2030, 13.9],
              [2050, 19.1],
            ],
            className: "color-gray  highcharts-translucent",
          },
        ],
        APS: [
          {
            name: "Traditional use of biomass",
            data: [
              [2021, 24],
              [2030, 9],
              [2050, 6],
            ],
            className: "color-historic",
          },
          {
            name: "Buildings and agriculture",
            data: [
              [2021, 4.8],
              [2030, 8],
              [2050, 8],
            ],
            className: "color-green",
          },
          {
            name: "Industry",
            data: [
              [2021, 11],
              [2030, 14],
              [2050, 19],
            ],
            className: "color-yellow",
          },
          {
            name: "Power",
            data: [
              [2021, 9.7],
              [2030, 15.2],
              [2050, 31.4],
            ],
            className: "color-blue",
          },
          {
            name: "Conversion losses",
            data: [
              [2021, 10.3],
              [2030, 16],
              [2050, 22],
            ],
            className: "color-gray highcharts-translucent",
          },
        ],
        NZE: [
          {
            name: "Traditional use of biomass",
            data: [
              [2021, 24],
              [2030, 0],
              [2050, 0],
            ],
            className: "color-historic",
          },
          {
            name: "Buildings and agriculture",
            data: [
              [2021, 4.8],
              [2030, 11],
              [2050, 7],
            ],
            className: "color-green",
          },
          {
            name: "Industry",
            data: [
              [2021, 11],
              [2030, 15],
              [2050, 20],
            ],
            className: "color-yellow",
          },
          {
            name: "Power",
            data: [
              [2021, 9.7],
              [2030, 16.7],
              [2050, 34.5],
            ],
            className: "color-blue",
          },
          {
            name: "Conversion losses",
            data: [
              [2021, 10.3],
              [2030, 15],
              [2050, 12],
            ],
            className: "color-gray highcharts-translucent",
          },
        ],
      },
    };
  },
};
</script>

<style scoped>
@import url("../css/main.css");
</style>
